if (!customElements.get('personalization-popup')) {
  customElements.define(
    'personalization-popup',
    class PersonalizationPopup extends HTMLElement {
      static TOTAL_STEPS = 3;
      // Artwork height of a flat capital (A, B, E…); taller glyphs (overshoots, Q's tail) scale against it.
      static LETTER_BASE_HEIGHT = 1397;
      static ZOOM_SCALE = 3;

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        const configEl = this.querySelector('[data-psn-config]');
        if (!this.dialog || !configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.error('personalization-popup: invalid config', error);
          return;
        }

        // Grouped in Liquid: [{ name, colors: [{ name, hexes: [hex1, hex2] }] }].
        this.types = this.config.types;
        if (this.types.length === 0) return;

        // SVG letters from snippets/personalization-letter, keyed by character.
        const letterTemplates = this.querySelector('[data-psn-letter-templates]');
        this.letterSvgs = new Map(
          [...(letterTemplates?.content.querySelectorAll('svg[data-letter]') || [])].map((svg) => [svg.dataset.letter, svg]),
        );

        this.strings = this.config.strings;
        this.maxLetters = this.config.maxLetters || 2;
        this.state = { step: 1, typeIndex: 0, letters: '', colorIndex: 0 };

        this.lettersEl = this.querySelector('[data-psn-letters]');
        this.input = this.querySelector('[data-psn-input]');
        this.message = this.querySelector('[data-psn-message]');
        this.swatchGroups = this.querySelectorAll('[data-psn-swatches]');
        this.colorName = this.querySelector('[data-psn-color-name]');
        this.progressBar = this.querySelector('[data-psn-progress-bar]');
        this.progressText = this.querySelector('[data-psn-progress-text]');
        this.error = this.querySelector('[data-psn-error]');
        this.backButton = this.querySelector('[data-psn-back]');
        this.nextButton = this.querySelector('[data-psn-next]');
        this.steps = this.querySelectorAll('[data-psn-step]');

        this.querySelector('[data-psn-close]').addEventListener('click', () => this.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        // showModal() already makes the page inert; this keeps Tab cycling inside the dialog
        // instead of escaping to the browser UI after the last control.
        this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
        this.onViewportChange = () => this.syncViewport();
        this.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('psn-open');
          this.unwatchViewport();
          if (this.mediaObserver) this.mediaObserver.disconnect();
          this.reset();
        });

        this.media = this.querySelector('.psn__media');
        this.canvas = this.querySelector('[data-psn-canvas]');
        this.mainImage = this.querySelector('.psn__image');
        this.mediaBg = this.querySelector('[data-psn-media-bg]');
        this.mainImage.addEventListener('load', () => this.drawMediaBackground());

        this.canvas.addEventListener('transitionend', (event) => {
          if (event.target === this.canvas && event.propertyName === 'transform') this.settleZoom();
        });

        this.zoomButton = this.querySelector('[data-psn-zoom]');
        this.zoomButton.addEventListener('click', () => this.setZoom(!this.isZoomed));
        // Clicking or tapping the photo zooms too, whenever the button is available. Pointer shortcut only:
        // the button stays the keyboard and screen reader control, so the photo isn't a second tab stop.
        this.media.addEventListener('click', (event) => {
          if (this.zoomButton.hidden || event.target.closest('[data-psn-zoom]')) return;
          this.setZoom(!this.isZoomed);
        });
        // Start fetching the sharp file as soon as zoom looks likely, so it's ready by the end of the animation.
        this.zoomButton.addEventListener('pointerenter', () => this.loadZoomImage(), { once: true });
        this.zoomButton.addEventListener('focus', () => this.loadZoomImage(), { once: true });

        this.querySelectorAll('[data-psn-type]').forEach((radio) => {
          radio.addEventListener('change', () => this.selectType(Number(radio.value)));
        });
        this.querySelectorAll('[data-psn-color]').forEach((radio) => {
          radio.addEventListener('change', () => this.selectColor(Number(radio.value)));
        });
        this.input.addEventListener('input', () => this.onLettersInput());
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && !this.nextButton.disabled) this.next();
        });
        this.backButton.addEventListener('click', () => this.goTo(this.state.step - 1));
        this.nextButton.addEventListener('click', () => this.next());

        this.isReady = true;
        this.render();
      }

      get type() {
        return this.types[this.state.typeIndex] || this.types[0];
      }

      get color() {
        return this.type.colors[this.state.colorIndex] || this.type.colors[0];
      }

      get swatchGroup() {
        return this.swatchGroups[this.state.typeIndex];
      }

      // Characters accepted in the input: the ones with an SVG letter.
      get hasInvalidCharacters() {
        return [...this.state.letters].some((char) => !this.letterSvgs.has(char));
      }

      // Shown when extra letters were cut off or characters are invalid; only invalid characters block Next.
      get lettersError() {
        return this.lettersOverflow || this.hasInvalidCharacters ? this.strings.errorMaxLetters : null;
      }

      get lettersValid() {
        return this.state.letters.length > 0 && !this.hasInvalidCharacters;
      }

      // Drops characters without an SVG letter (digits, symbols, spaces; typed or pasted), then caps the
      // input at maxLetters, flagging the overflow so the error shows until the next edit within the limit.
      onLettersInput() {
        const supported = [...this.input.value].filter((char) => this.letterSvgs.has(char.toUpperCase())).join('');
        if (supported !== this.input.value) this.input.value = supported;
        const chars = [...this.input.value];
        this.lettersOverflow = chars.length > this.maxLetters;
        if (this.lettersOverflow) this.input.value = chars.slice(0, this.maxLetters).join('');
        this.state.letters = this.input.value.toUpperCase();
        this.render();
      }

      open() {
        if (!this.isReady) return;
        this.hideError();
        // Images stay lazy until the popup is first opened, then load right away.
        this.dialog.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
        document.documentElement.classList.add('psn-open');
        this.dialog.showModal();
        this.watchViewport();
        // After showModal, which would otherwise focus the scrollable panel (an unnamed element).
        this.goTo(1);

        if (!this.mediaObserver) {
          this.mediaObserver = new ResizeObserver(() => {
            this.drawMediaBackground();
            this.applyZoom();
          });
        }
        this.mediaObserver.observe(this.media);
      }

      // Tripled `sizes` makes the browser pick a srcset file sharp enough for the zoom. It stays that way
      // after zooming out: the file is already loaded and swapping back would only refetch the small one.
      loadZoomImage() {
        const { zoomSizes } = this.mainImage.dataset;
        if (zoomSizes && this.mainImage.sizes !== zoomSizes) this.mainImage.sizes = zoomSizes;
      }

      setZoom(isZoomed) {
        if (isZoomed) this.loadZoomImage();
        this.isZoomed = isZoomed;
        this.zoomButton.setAttribute('aria-pressed', String(isZoomed));
        this.media.classList.toggle('is-zoomed', isZoomed);
        this.applyZoom();
      }

      // The canvas's unzoomed layout box in the media area. While settled the canvas is laid out at the zoomed
      // size, so the box measured just before settling is used instead.
      get canvasBox() {
        if (this.settledBox) return this.settledBox;
        const { offsetLeft: left, offsetTop: top, offsetWidth: width, offsetHeight: height } = this.canvas;
        return { left, top, width, height };
      }

      // Scales the photo and letters up and moves the letters' spot (the image focal point) to the middle
      // of the media area, stopping where a photo edge would come inside the frame. The scale animates as a
      // transform; settleZoom then swaps it for the real zoomed size once the animation ends.
      applyZoom() {
        const scale = PersonalizationPopup.ZOOM_SCALE;

        // Back from the settled layout to the same picture drawn with the scale transform, without animating,
        // so whatever comes next (zoom out, or a new position after a resize) animates from there.
        if (this.settledBox) {
          const { x, y } = this.zoomShift;
          this.canvas.style.transition = 'none';
          this.unsettleZoom();
          this.canvas.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
          this.canvas.getBoundingClientRect();
          this.canvas.style.transition = '';
        }

        if (!this.isZoomed) {
          this.canvas.style.transform = '';
          return;
        }

        const focusX = parseFloat(this.lettersEl.style.left) / 100;
        const focusY = parseFloat(this.lettersEl.style.top) / 100;
        const box = this.canvasBox;

        // Translation along one axis; with transform-origin 0 0 a canvas point p lands at offset + shift + p * scale.
        const shiftFor = (offset, size, focus, frame) => {
          const scaled = size * scale;
          if (scaled <= frame) return (frame - scaled) / 2 - offset;
          const centered = frame / 2 - offset - focus * scaled;
          return Math.min(-offset, Math.max(frame - scaled - offset, centered));
        };
        const x = shiftFor(box.left, box.width, focusX, this.media.clientWidth);
        const y = shiftFor(box.top, box.height, focusY, this.media.clientHeight);
        this.zoomShift = { x, y };

        const transform = `translate(${x}px, ${y}px) scale(${scale})`;
        const willAnimate =
          this.canvas.style.transform !== transform && parseFloat(getComputedStyle(this.canvas).transitionDuration) > 0;
        this.canvas.style.transform = transform;
        // No transition to wait for (reduced motion, or already in place): settle right away.
        if (!willAnimate) this.settleZoom();
      }

      // A scaled layer is drawn at its original size and then stretched, so on phones the SVG letters (and the
      // photo) look blurry at 3x. Once zoomed, the canvas is laid out at the zoomed size and only translated:
      // the same picture, but the browser redraws it at full resolution. The canvas leaves the flow for this,
      // so the media area keeps its height.
      settleZoom() {
        if (this.settledBox || !this.isZoomed) return;
        const box = this.canvasBox;
        const { x, y } = this.zoomShift;
        this.media.style.height = `${this.media.offsetHeight}px`;
        this.settledBox = box;
        this.canvas.classList.add('is-zoom-settled');
        this.canvas.style.width = `${box.width * PersonalizationPopup.ZOOM_SCALE}px`;
        this.canvas.style.transform = `translate(${box.left + x}px, ${box.top + y}px)`;
      }

      unsettleZoom() {
        if (!this.settledBox) return;
        this.settledBox = null;
        this.canvas.classList.remove('is-zoom-settled');
        this.canvas.style.width = '';
        this.media.style.height = '';
      }

      disconnectedCallback() {
        if (this.mediaObserver) this.mediaObserver.disconnect();
        this.unwatchViewport();
      }

      // On phones the on-screen keyboard (step 2 focuses the input) and the browser toolbars shrink the visible
      // area without changing 100dvh, which leaves the sticky footer under the keyboard or above an empty strip.
      // While open, the dialog takes the visual viewport's height and follows its offset, so the footer stays
      // on the bottom edge of what's actually visible.
      watchViewport() {
        const viewport = window.visualViewport;
        if (!viewport) return;
        viewport.addEventListener('resize', this.onViewportChange);
        viewport.addEventListener('scroll', this.onViewportChange);
        this.syncViewport();
      }

      unwatchViewport() {
        const viewport = window.visualViewport;
        if (viewport) {
          viewport.removeEventListener('resize', this.onViewportChange);
          viewport.removeEventListener('scroll', this.onViewportChange);
        }
        this.dialog.style.removeProperty('--psn-h');
        this.dialog.style.removeProperty('translate');
      }

      syncViewport() {
        const viewport = window.visualViewport;
        if (!viewport || !this.dialog.open) return;
        // Pinch zoom also shrinks the visual viewport; keep the regular size then.
        if (Math.abs(viewport.scale - 1) > 0.01) {
          this.dialog.style.removeProperty('--psn-h');
          this.dialog.style.removeProperty('translate');
          return;
        }
        this.dialog.style.setProperty('--psn-h', `${viewport.height}px`);
        this.dialog.style.translate = viewport.offsetTop ? `0 ${viewport.offsetTop}px` : '';
      }

      // Product photos aren't a flat color (vignettes, uneven light), so the area around the photo is
      // painted by stretching the photo's outermost pixels to the panel edges; CSS blur smooths the streaks.
      async drawMediaBackground() {
        const img = this.mainImage;
        if (!this.mediaBg || !img.complete || !img.currentSrc || !this.dialog.open) return;

        // An <img> with srcset reports density-corrected natural sizes, but drawImage reads source
        // coordinates in real bitmap pixels, so edges are sampled from a plain Image of the same file.
        if (!this.edgeSource || this.edgeSource.src !== img.currentSrc) {
          const image = new Image();
          image.src = img.currentSrc;
          try {
            await image.decode();
          } catch (error) {
            return;
          }
          this.edgeSource = image;
        }
        const source = this.edgeSource;

        const mediaRect = this.mediaBg.getBoundingClientRect();
        // The photo's unzoomed layout box (it fills the canvas), so the zoom doesn't skew it.
        const mediaBox = this.media.getBoundingClientRect();
        const box = this.canvasBox;
        const imageRect = {
          left: mediaBox.left + box.left,
          top: mediaBox.top + box.top,
          width: box.width,
          height: box.height,
        };
        if (!mediaRect.width || !imageRect.width) return;

        const scale = 0.25;
        const canvas = this.mediaBg;
        canvas.width = Math.ceil(mediaRect.width * scale);
        canvas.height = Math.ceil(mediaRect.height * scale);

        // Where the photo sits on the canvas, and the size of the empty strips around it.
        const photoLeft = (imageRect.left - mediaRect.left) * scale;
        const photoTop = (imageRect.top - mediaRect.top) * scale;
        const photoWidth = imageRect.width * scale;
        const photoHeight = imageRect.height * scale;
        const photoRight = photoLeft + photoWidth;
        const photoBottom = photoTop + photoHeight;
        const rightStripWidth = Math.max(0, canvas.width - photoRight);
        const bottomStripHeight = Math.max(0, canvas.height - photoBottom);

        // Edge pixels of the source file, sampled a little inside the edge to skip compression artifacts.
        const sourceWidth = source.naturalWidth;
        const sourceHeight = source.naturalHeight;
        const inset = Math.max(1, Math.round(Math.min(sourceWidth, sourceHeight) * 0.004));
        const leftEdgeX = inset;
        const rightEdgeX = sourceWidth - inset - 1;
        const topEdgeY = inset;
        const bottomEdgeY = sourceHeight - inset - 1;

        // drawImage(image, sourceX, sourceY, sourceW, sourceH, canvasX, canvasY, canvasW, canvasH)
        // copies a source region onto a canvas region, stretching it to fit.
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Sides: a 1px column or row from each edge, stretched across its strip.
        context.drawImage(source, leftEdgeX, 0, 1, sourceHeight, 0, photoTop, photoLeft, photoHeight);
        context.drawImage(source, rightEdgeX, 0, 1, sourceHeight, photoRight, photoTop, rightStripWidth, photoHeight);
        context.drawImage(source, 0, topEdgeY, sourceWidth, 1, photoLeft, 0, photoWidth, photoTop);
        context.drawImage(source, 0, bottomEdgeY, sourceWidth, 1, photoLeft, photoBottom, photoWidth, bottomStripHeight);

        // Corners: a single corner pixel, stretched over the corner area.
        context.drawImage(source, leftEdgeX, topEdgeY, 1, 1, 0, 0, photoLeft, photoTop);
        context.drawImage(source, rightEdgeX, topEdgeY, 1, 1, photoRight, 0, rightStripWidth, photoTop);
        context.drawImage(source, leftEdgeX, bottomEdgeY, 1, 1, 0, photoBottom, photoLeft, bottomStripHeight);
        context.drawImage(source, rightEdgeX, bottomEdgeY, 1, 1, photoRight, photoBottom, rightStripWidth, bottomStripHeight);

        // The photo itself on top.
        context.drawImage(source, photoLeft, photoTop, photoWidth, photoHeight);
      }

      close() {
        this.dialog.close();
      }

      // Tab stops in DOM order; hidden steps and buttons drop out (no offsetParent), and in a radio
      // group only the checked radio is a Tab stop, so unchecked ones are skipped.
      getFocusable() {
        return [
          ...this.dialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ].filter((el) => el.offsetParent !== null && !(el.type === 'radio' && !el.checked));
      }

      trapFocus(event) {
        if (event.key !== 'Tab') return;
        const focusable = this.getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        // Focus on the dialog itself or anything outside the list wraps like it was on an edge.
        const isOutside = !focusable.includes(active);
        if (event.shiftKey && (active === first || isOutside)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || isOutside)) {
          event.preventDefault();
          first.focus();
        }
      }

      // Runs on every close (button, Esc, backdrop, after add to cart) so the next open starts fresh.
      reset() {
        this.state = { step: 1, typeIndex: 0, letters: '', colorIndex: 0 };
        this.input.value = '';
        this.lettersOverflow = false;
        this.setZoom(false);
        const firstType = this.querySelector('[data-psn-type]');
        if (firstType) firstType.checked = true;
        this.render();
      }

      selectType(index) {
        if (index === this.state.typeIndex) return;
        this.state.typeIndex = index;
        this.state.colorIndex = 0;
        this.render();
      }

      selectColor(index) {
        this.state.colorIndex = index;
        this.render();
      }

      goTo(step) {
        this.state.step = Math.min(Math.max(step, 1), PersonalizationPopup.TOTAL_STEPS);
        this.lettersOverflow = false;
        this.hideError();
        this.render();

        // Move focus to the step's control so keyboard and screen reader users land in the new step
        // (the Back button hides itself on step 1, which would otherwise drop focus to the page).
        // A single type has no radio to focus, so step 1 then lands on Next.
        if (this.state.step === 1 && !this.focusChecked(this.querySelector('.psn__types'))) this.nextButton.focus();
        if (this.state.step === 2) this.input.focus();
        if (this.state.step === 3) this.focusChecked(this.swatchGroup);
      }

      focusChecked(group) {
        const radio = group && (group.querySelector('input:checked') || group.querySelector('input'));
        if (radio) radio.focus();
        return Boolean(radio);
      }

      next() {
        if (this.nextButton.disabled || this.isAdding) return;
        if (this.state.step < PersonalizationPopup.TOTAL_STEPS) {
          this.goTo(this.state.step + 1);
        } else {
          this.addToCart();
        }
      }

      render() {
        const { step } = this.state;
        const isLastStep = step === PersonalizationPopup.TOTAL_STEPS;

        this.steps.forEach((el) => {
          el.hidden = Number(el.dataset.psnStep) !== step;
        });

        this.progressBar.style.width = `${(step / PersonalizationPopup.TOTAL_STEPS) * 100}%`;
        this.progressText.textContent = this.strings.progress.replace('[step]', step);
        this.backButton.hidden = step === 1;
        // Zoom is for checking the initials, so it only shows from step 2; back on step 1 the photo zooms out.
        this.zoomButton.hidden = step === 1;
        this.media.classList.toggle('is-zoomable', step !== 1);
        if (step === 1 && this.isZoomed) this.setZoom(false);
        this.nextButton.textContent = isLastStep ? this.strings.addToCart : this.strings.next;
        this.nextButton.disabled = step > 1 && !this.lettersValid;

        const error = this.lettersError;
        const message = error || this.strings.hint;
        if (this.message.textContent !== message) this.message.textContent = message;
        this.message.classList.toggle('is-error', Boolean(error));
        this.input.classList.toggle('is-error', Boolean(error));
        this.input.setAttribute('aria-invalid', String(Boolean(error)));

        this.colorName.textContent = this.color.name;
        this.renderSwatches();
        this.renderPreview();
      }

      // Swatches are rendered in Liquid, one group per type: show the selected type's group and keep
      // its checked radio in sync with the state (it resets to the first color on type change and close).
      renderSwatches() {
        this.swatchGroups.forEach((group, index) => {
          group.hidden = index !== this.state.typeIndex;
        });
        const radio = this.swatchGroup?.querySelectorAll('[data-psn-color]')[this.state.colorIndex];
        if (radio) radio.checked = true;
      }

      // Letter is hex1, its offset shadow hex2 (or hex1 again when the color has one hex).
      renderPreview() {
        const [color1, color2 = color1] = this.color.hexes;
        this.lettersEl.style.setProperty('--psn-letter-color-1', color1 || '');
        this.lettersEl.style.setProperty('--psn-letter-color-2', color2 || '');

        this.lettersEl.replaceChildren();
        if (this.state.step === 1 || !this.lettersValid) return;

        [...this.state.letters].forEach((char) => {
          const svg = this.letterSvgs.get(char).cloneNode(true);
          svg.style.setProperty('--psn-letter-scale', svg.viewBox.baseVal.height / PersonalizationPopup.LETTER_BASE_HEIGHT);
          this.lettersEl.appendChild(svg);
        });
      }

      get sectionsToUpdate() {
        return window.location.pathname.includes('/cart') ? 'cart-icon-bubble,main-cart-extend' : 'cart-icon-bubble,cart-drawer';
      }

      // Adds the main product with the line properties read by the vee-personalization
      // Cart Transform, which expands the line into main product + personalize price product.
      async addToCart() {
        const form = document.getElementById(this.dataset.formId);
        if (!form) return;

        const data = new FormData(form);
        const [initialsLabel, colorLabel, typeLabel] = this.config.optionLabels;
        data.set(`properties[${initialsLabel}]`, this.state.letters);
        data.set(`properties[${colorLabel}]`, this.color.name);
        data.set('properties[_personalization]', this.dataset.personalizationVariantId);
        data.set('properties[_personalizationPrice]', this.dataset.personalizationPrice);

        // Initials, Color (and Type) show in the cart drawer as line properties; _selectedOptions/_selectedValues
        // are copied by the cart transform onto the bundle's lines in checkout. Type only when there's a choice
        // of types: with a single type it would just repeat the same value on every order.
        const selected = [
          [initialsLabel, this.state.letters],
          [colorLabel, this.color.name],
        ];
        if (this.types.length > 1) {
          data.set(`properties[${typeLabel}]`, this.type.name);
          selected.push([typeLabel, this.type.name]);
        }
        data.set('properties[_selectedOptions]', selected.map(([label]) => label).join('|'));
        data.set('properties[_selectedValues]', selected.map(([, value]) => value).join('|'));
        data.set('sections', this.sectionsToUpdate);
        data.set('sections_url', window.location.pathname);

        this.hideError();
        // aria-disabled instead of disabled: disabling the focused button would drop focus to the page.
        this.isAdding = true;
        this.nextButton.setAttribute('aria-disabled', 'true');
        this.nextButton.classList.add('is-loading');

        try {
          const response = await fetch(theme.routes.cart_add_url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/javascript' },
            body: data,
          });
          const result = await response.json();
          if (!response.ok) throw result;

          this.close();
          document.dispatchEvent(
            new CustomEvent('switch:cart:afteradditem', {
              bubbles: true,
              detail: { response: result },
            }),
          );
        } catch (error) {
          this.showError((error && (error.description || error.message)) || this.strings.genericError);
        } finally {
          this.isAdding = false;
          this.nextButton.removeAttribute('aria-disabled');
          this.nextButton.classList.remove('is-loading');
        }
      }

      showError(message) {
        this.error.textContent = message;
      }

      hideError() {
        this.error.textContent = '';
      }
    },
  );
}
